/**
 * Wire shapes returned by the Laravel backend.
 *
 * The API speaks TWO different response shapes and the client must handle both:
 *
 *  1. The `sendAjaxResponse()` envelope — every deliberate success and error
 *     from the application, plus the framework exceptions rendered in
 *     bootstrap/app.php (401, 403, 404, 429).
 *  2. Laravel's validation body — 422s raised by Form Requests, which do NOT
 *     use the envelope.
 *
 * They are told apart by structure, not by status code: a rejected password
 * reset token deliberately returns a 422 *envelope* with no `errors` key.
 * See `toAppError` in @/api/errors.
 */

/** The standard `sendAjaxResponse()` envelope. */
export interface ApiEnvelope<TData = unknown> {
  code: number;
  status: 'success' | 'error';
  data: TData;
  message: string;
}

/** Laravel's 422 body from a failed Form Request. */
export interface ValidationErrorBody {
  message: string;
  /** Keyed by field path, e.g. `{ email: ['The email field is required.'] }`. */
  errors: Record<string, string[]>;
}

export function isApiEnvelope(body: unknown): body is ApiEnvelope {
  return (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    'status' in body &&
    'message' in body
  );
}

export function isValidationErrorBody(body: unknown): body is ValidationErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    'errors' in body &&
    typeof body.errors === 'object' &&
    body.errors !== null
  );
}
